import { Controller } from "@hotwired/stimulus"

// OTP Input Controller — 6-digit verification flow
export default class extends Controller {
  static targets = ["input", "hidden", "submit"]

  connect() {
    // Auto-focus first input on page load
    requestAnimationFrame(() => {
      this.inputTargets[0]?.focus()
    })

    // Handle browser auto-fill (e.g. password manager)
    this._handlePastedFocus()
  }

  // ---- Stimulus Action Handlers ----

  onInput(e) {
    const input = e.target
    const value = input.value.replace(/[^0-9]/g, "")
    input.value = value.charAt(0) || ""

    this._updateHidden()

    // Move to next input after entering a digit
    if (value) {
      const idx = this.inputTargets.indexOf(input)
      if (idx < this.inputTargets.length - 1) {
        this.inputTargets[idx + 1].focus()
      }
    }

    this._checkComplete()
  }

  onKeydown(e) {
    const input = e.target
    const idx = this.inputTargets.indexOf(input)

    // Backspace: clear current and move to previous
    if (e.key === "Backspace" && !input.value && idx > 0) {
      this.inputTargets[idx - 1].focus()
      this.inputTargets[idx - 1].value = ""
      this._updateHidden()
    }

    // Arrow keys for navigation between inputs
    if (e.key === "ArrowLeft" && idx > 0) {
      this.inputTargets[idx - 1].focus()
    } else if (e.key === "ArrowRight" && idx < this.inputTargets.length - 1) {
      this.inputTargets[idx + 1].focus()
    }

    // Auto-select all when focusing via click
    input.addEventListener("focus", () => input.select(), { once: true })
  }

  onPaste(e) {
    e.preventDefault()
    const pasted = (e.clipboardData || window.clipboardData).getData("text")
    const digits = pasted.replace(/[^0-9]/g, "").slice(0, 6)

    this.inputTargets.forEach((input, i) => {
      input.value = digits[i] || ""
    })

    this._updateHidden()

    // Focus the last filled input or the first empty one
    const focusIndex = Math.min(digits.length, this.inputTargets.length - 1)
    this.inputTargets[focusIndex].focus()

    this._checkComplete()
  }

  _handlePastedFocus() {
    // If browser auto-filled (e.g. password manager), handle it
    const code = document.querySelector('[autocomplete="one-time-code"]')
    if (code?.value) {
      this._distributeAutoFilled()
    }
  }

  _distributeAutoFilled() {
    const firstInput = this.inputTargets[0]
    const value = (firstInput?.value || "").replace(/[^0-9]/g, "")
    if (value.length > 1) {
      this.inputTargets.forEach((input, i) => {
        input.value = value[i] || ""
      })
      this._updateHidden()
      this._checkComplete()
    }
  }

  // ---- Hidden Code Sync ----

  _updateHidden() {
    const code = this.inputTargets.map((t) => t.value).join("")
    this.hiddenTarget.value = code
  }

  // ---- Auto-Submit on 6 Digits ----

  _checkComplete() {
    const code = this._getCode()
    if (code.length === 6) {
      // Small delay so the last input visually registers before submit
      setTimeout(() => this._autoSubmit(), 150)
    }
  }

  _getCode() {
    return this.inputTargets.map((t) => t.value).join("")
  }

  _autoSubmit() {
    const form = this.hiddenTarget.closest("form")
    if (form) form.requestSubmit()
  }
}
